// eslint-disable-next-line import/no-cycle
import {AdvancedPromotions, BasketItemType, PricesModel, ShippingFeeRule} from 'types/basket';

import {BillingAddress, ShippingAddress} from './addresses';
import {StoreInfo} from './multistore';
import {Product} from './product';

export type LineItem = {
  variantId: string;
  quantity: number;
  productDetails?: Product;
  outsideOfDeliveryZone?: boolean;
  price?: number;
  sku?: string;
  status?: string;
};

export interface CustomAttribute {
  key: string;
  value: string;
}

export enum CheckoutStatus {
  DRAFT = 'DRAFT',
  IN_PROGRESS = 'IN_PROGRESS',
  PAYING = 'PAYING',
  COMPLETED = 'COMPLETED',
  ABANDONED = 'ABANDONED'
}

export interface DeliverySlot {
  id: string;
  date: string;
  from: string;
  to: string;
  __typename?: string;
}

export interface ISlotsState {
  slotsFor1P: SlotV2[];
  slotsFor1PBulky: SlotV2[];
  slotsFor1PX: SlotV2[];
  slotsFor3PMF: SlotV2[];
}

export interface IPickedSlotsState {
  delivery1PSlotPicked: DeliverySlot | undefined;
  delivery1PBulkySlotPicked: DeliverySlot | undefined;
  delivery1PXSlotPicked: DeliverySlot | undefined;
  delivery3PMFSlotPicked: Record<string, DeliverySlot>;
}

export interface ICheckoutTypes {
  has1pSeller: boolean;
  hasMakroFleetSeller: boolean;
  has1pBulky: boolean;
  has1px: boolean;
}

export interface DropShipDeliverySlot {
  sellerName: string;
  deliveryTimeEarliestDate: string;
  deliveryTimeLatestDate: string;
}

export interface BasketProductPromotion {
  productId: string;
  priceBeforePromotion: number;
  promotionIds: string[];
  complexPromotionIds: string[];
  discountAmount: number;
  unitPriceBeforePromotion: number;
}

export declare type ShopModel = {
  shopId: number;
  shopName: string;
  fulfilledByPlatform?: string;
  marketplaceType?: string;
};

export interface BasketProductVariants {
  moq?: number;
}

export interface BasketProduct {
  basketProductId: string;
  variantId: string;
  quantity: number;
  storeCode?: string;
  status: string;
  size: string;
  originPrice: number;
  discountPrice: number;
  promotedPrice: number;
  promotion: BasketProductPromotion;
  sellerDetails?: Partial<ShopModel>;
  title: string;
  // inventoryQuantity: number;
  imageUrls: string[];
  metafields: CustomAttribute[];
  deliverySlot: DropShipDeliverySlot;
  discountId: string;
  discountAmount: number;
  discountApplied: boolean;
  outsideOfDeliveryZone: boolean;
  isBulky: boolean;
  is1PX?: boolean;
  isWeightedFresh?: boolean;
  isAlcohol?: boolean;
  installmentType?: string;
  isPreOrder?: boolean;
  sku: string;
  quantityInUnits?: number;
  weightedFreshPricePerUnit?: number;
  priceUnit: string;
  variants?: BasketProductVariants[];
}

export interface LoyaltyDetails {
  referenceId: string;
  redeemPoints: number;
  remainingPoints: number;
  estimatedPointsReceive: number;
  discountCodeId: number;
  priceRuleId: number;
  code: string;
  value: number;
  transactionId: string;
  amount: number;
  data: string;
  maxRedeemablePoints: number;
}

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  displayName?: string;
  email: string;
  mobile: string;
}

export interface Checkout {
  id: string;
  status: string | CheckoutStatus;
  voucher: string;
  loyaltyPoints: number;
  basketId: string;
  customerId: string;
  prices: PricesModel;
  discountAmount: number;
  currencyIsoCode: string;
  customerEmail: string;
  orderId: string;
  deliverySlot: DeliverySlot;
  lineItems: BasketProduct[];
  billingAddress: BillingAddress;
  shippingAddress: ShippingAddress;
  paymentURL: string;
  customAttributes: CustomAttribute[];
  dateCreated: string;
  dateModified: string;
  checkoutURL: string;
  loyaltyDetails: LoyaltyDetails;
  advancedPromotions: AdvancedPromotions[];
  zipCode?: string;
  subDistrict?: string;
  currentStore?: StoreInfo;
  storeCode?: string;
  customerInfo?: CustomerInfo;
  freeShippingValue?: number;
  shippingFeeRule?: {
    shippingRanges: ShippingFeeRule[];
    minimumBasket?: number;
  };
}

export interface SlotData {
  id: string;
  date: string;
  from: string;
  to: string;
  isAvailable: boolean;
  isSameDay: boolean;
}

export interface CheckoutDataForLocale {
  id: string;
  customAttributes: CustomAttribute[];
  __typename: string;
}

export interface UpdateCheckoutLocaleResponse {
  data?: {
    checkout: CheckoutDataForLocale;
  };
}

export interface UpdateShippingAddressResponse {
  data: {address: {shippingAddress: ShippingAddress}};
}

export interface UpdateBillingAddressResponse {
  data: {address: {shippingAddress: ShippingAddress}};
}
export interface getSlotsResponse {
  data: {
    slotsByStoreCode: Array<SlotData>;
  };
  loading?: boolean;
  networkStatus?: number;
}

export enum DeliverySlotUpdatingErrorCode {
  FULLY_BOOKED = 'FULLY_BOOKED',
  UNKNOWN = 'UNKNOWN',
  GET_SLOTS_ERROR = 'GET_SLOTS_ERROR'
}

export enum VoucherErrorCode {
  VOUCHER_REMOVAL_FAILED = 'VOUCHER_REMOVAL_FAILED',
  VOUCHER_APPLICABILITY_FAILED = 'VOUCHER_APPLICABILITY_FAILED'
}

export enum LoyalityPointsErrorCode {
  LOYALITY_POINTS_REMOVAL_FAILED = 'LOYALITY_POINTS_REMOVAL_FAILED',
  LOYALITY_POINTS_APPLICABILITY_FAILED = 'LOYALITY_POINTS_APPLICABILITY_FAILED'
}

export enum ApiTypes {
  GET_DELIVERY_SLOTS = 'GET_DELIVERY_SLOTS',
  UPDATE_DELIVERY_SLOT = 'UPDATE_DELIVERY_SLOT',
  GET_CHECKOUT_BY_ID = 'GET_CHECKOUT_BY_ID',
  COMPLETE_CHECKOUT = 'COMPLETE_CHECKOUT'
}

export interface CreateOrderResult {
  orderId: string;
  displayId: string;
}

export interface CustomAttribute {
  key: string;
  value: string;
}

export interface ApplyLoyaltyAndVoucherInput {
  checkoutId: string;
  makroCardId?: string;
  loyaltyPoints?: number;
  voucherId?: string;
}

export interface PartialCheckoutCreateInput {
  zipCode: string;
  subDistrict: string;
  customAttributes: [CustomAttribute];
}
export interface UpdateCheckoutDeliverySlotResponse {
  deliverySlot: DeliverySlot;
  prices?: PricesModel;
}

export interface AllDeliverySlots {
  deliverySlot: DeliverySlot;
  shopId: number;
  deliveryItemType: BasketItemType;
}

export interface UpdateCheckoutAllDeliverySlotResponse {
  allDeliverySlot: AllDeliverySlots[];
  prices?: PricesModel;
}

export interface UpdateCheckoutDeliverySlotPreOrderResponse {
  deliverySlotPreOrder: DeliverySlot;
}

export interface TimeSlot {
  id: string;
  time: string;
  isAvailable: boolean;
  isSameDay: boolean;
}
export interface Slot {
  date: string;
  displayDate?: string;
  timeSlots: TimeSlot[];
  numberOfTimeSlots: number;
}

export interface SlotV2 extends Slot {
  weekDay: string;
  isSameDay: boolean;
}

export interface UpdateDeliverySlotPayload {
  slotId: string;
  isSameDay: boolean;
  isStorePickUpFeatureEnabled: boolean;
  sellerId?: number;
  deliveryType?: BasketItemType;
}

export enum CheckoutDeliveryMethod {
  STORE_PICKUP = 'STORE_PICKUP',
  DOOR_STEP = 'DOOR_STEP'
}

export interface UpdateCheckoutAndDeliveySlotActionPayload {
  deliverySlot: DeliverySlot;
  checkout: Checkout;
}
export interface ApiErrorPayload {
  message: string;
  apiName: string;
  payload?: {
    [key: string]: string;
  };
}
export type VerifyVoucherResponse = {
  code: string;
  amount: number;
  isLoyaltyVoucher: boolean;
  referenceId?: string;
  remainingPoints?: number;
};

export interface AreaModel {
  zipcode: number;
  subdistrict: string;
  subdistrictEn: string;
  status: string;
}

export const CHECKOUT_EVENTS = {
  CHECKOUT_CREATED: 'CHECKOUT_CREATED',
  CHECKOUT_GET_DELIVERY_SLOTS: 'CHECKOUT_GET_DELIVERY_SLOTS',
  CHECKOUT_GET_DELIVERY_SLOTS_FAILED: 'CHECKOUT_GET_DELIVERY_SLOTS_FAILED',
  CHECKOUT_UPDATE_DELIVERY_SLOT: 'CHECKOUT_UPDATE_DELIVERY_SLOT',
  CHECKOUT_UPDATE_DELIVERY_SLOT_FAILED: 'CHECKOUT_UPDATE_DELIVERY_SLOT_FAILED',
  CHECKOUT_UPDATE_PRE_ORDER_DELIVERY_SLOT: 'CHECKOUT_UPDATE_PRE_ORDER_DELIVERY_SLOT',
  CHECKOUT_UPDATE_PRE_ORDER_DELIVERY_SLOT_FAILED: 'CHECKOUT_UPDATE_PRE_ORDER_DELIVERY_SLOT_FAILED',
  CHECKOUT_CREATE_ADDRESS: 'CHECKOUT_CREATE_ADDRESS',
  CHECKOUT_CREATE_ADDRESS_FAILED: 'CHECKOUT_CREATE_ADDRESS_FAILED',
  CHECKOUT_EDIT_ADDRESS: 'CHECKOUT_EDIT_ADDRESS',
  CHECKOUT_EDIT_ADDRESS_FAILED: 'CHECKOUT_EDIT_ADDRESS_FAILED',
  CHECKOUT_UPDATE_BILLING_ADDRESS: 'CHECKOUT_UPDATE_BILLING_ADDRESS',
  CHECKOUT_UPDATE_BILLING_ADDRESS_FAILED: 'CHECKOUT_UPDATE_BILLING_ADDRESS_FAILED',
  CHECKOUT_UPDATE_SHIPPING_ADDRESS: 'CHECKOUT_UPDATE_SHIPPING_ADDRESS',
  CHECKOUT_UPDATE_SHIPPING_ADDRESS_FAILED: 'CHECKOUT_UPDATE_SHIPPING_ADDRESS_FAILED',
  CHECKOUT_UPDATE_LOCALE: 'CHECKOUT_UPDATE_LOCALE',
  CHECKOUT_UPDATE_LOCALE_FAILED: 'CHECKOUT_UPDATE_LOCALE_FAILED',
  CHECKOUT_APPLY_VOUCHER: 'CHECKOUT_APPLY_VOUCHER',
  CHECKOUT_APPLY_VOUCHER_FAILED: 'CHECKOUT_APPLY_VOUCHER_FAILED',
  CHECKOUT_REMOVE_VOUCHER: 'CHECKOUT_REMOVE_VOUCHER',
  CHECKOUT_REMOVE_VOUCHER_FAILED: 'CHECKOUT_REMOVE_VOUCHER_FAILED',
  CHECKOUT_REDEEM_POINTS: 'CHECKOUT_REDEEM_POINTS',
  CHECKOUT_REDEEM_POINTS_FAILED: 'CHECKOUT_REDEEM_POINTS_FAILED',
  CHECKOUT_CANCEL_POINTS: 'CHECKOUT_CANCEL_POINTS',
  CHECKOUT_CANCEL_POINTS_FAILED: 'CHECKOUT_CANCEL_POINTS_FAILED',
  CHECKOUT_COMPLETE: 'CHECKOUT_COMPLETE',
  CHECKOUT_COMPLETE_FAILED: 'CHECKOUT_COMPLETE_FAILED'
};

export enum CheckoutErrorMessage {
  INCOMPLETE_SHIPPING_ADDRESS = 'Shipping address details incomplete',
  INCOMPLETE_BILLING_ADDRESS = 'Billing address details incomplete',
  MISSING_DELIVERY_SLOT = 'Checkout missing delivery slot details',
  PRODUCT_WITH_INSUFFICIENT_QUANTITIES = 'Product with insufficient quantities',
  NO_CHECKOUT_FOUND = 'NO_CHECKOUT_FOUND',
  TOKEN_EXPIRED = 'Invalid authorization header'
}

export interface GetSlotsV2Response {
  itemType: BasketItemType;
  slots: SlotData[];
}

export interface ICheckoutSlots {
  [BasketItemType.DEFAULT]: SlotData[] | undefined;
  [BasketItemType.MAKRO_FLEET]: SlotData[] | undefined;
  [BasketItemType.ONEPX]: SlotData[] | undefined;
  [BasketItemType.ONEP_BULKY]: SlotData[] | undefined;
}

export interface IUpdateAllSlotsPayload {
  slotId: string;
  shopId: number;
  basketItemType: BasketItemType;
}

export interface INonRegular1PPickedSlots {
  delivery1PBulkySlotPicked: DeliverySlot | undefined;
  delivery1PXSlotPicked: DeliverySlot | undefined;
  delivery3PMFSlotPicked: Record<number, DeliverySlot>;
}

export interface INonRegular1PSlots {
  slotsFor1PBulky: SlotV2[] | undefined;
  slotsFor1PX: SlotV2[] | undefined;
  slotsFor3PMF: SlotV2[] | undefined;
}

export interface IGetSlotsActionPayload {
  onSuccess: (slots: INonRegular1PSlots) => Promise<void>;
}

export interface IUpdateDeliveryPickedSlotsActionPayload {
  response: UpdateCheckoutAllDeliverySlotResponse | UpdateCheckoutDeliverySlotResponse;
}

export enum CustomAttributeSource {
  APP = 'app',
  WEB = 'web'
}

export enum CustomAttributeKey {
  OVERALL_ORDER_STATUS = 'overall_order_status',
  DELIVERED_ON_TIMESTAMP = 'delivered_on_timestamp',
  LOCALIZED_THAI_DELIVERED_ON_TIMESTAMP = 'localized_thai_delivered_on_timestamp',
  COUNT_OF_LINE_ITEMS = 'count_of_line_items',
  PAYMENT_TYPE = 'payment_type',
  PAY_BY_TIMESTAMP = 'pay_by_timestamp',
  SHIPMENT_DETAILS = 'shipment_details',
  STORE_CODE = 'store_code',
  SHIP_DATE = 'Ship Date',
  PAID_ON_TIMESTAMP = 'paid_on_timestamp',
  BANK_ACCOUNT_NUMBER = 'bank_account_number',
  BANK_ACCOUNT_NAME = 'bank_account_name',
  BANK_NAME = 'bank_name',
  USER_ZIP_CODE = 'user_zip_code',
  BASKET_ITEMS = 'basket_items',
  ZIP_CODES_BY_STORES = 'zip_codes_by_stores',
  RESERVATION_ID = 'reservation_id',
  SHIPPING_FEE = 'shipping_fee',
  ACCESS_TOKEN = 'access_token',
  ID_TOKEN = 'id_token',
  SOURCE_TYPE = 'source_type',
  WEBAPP_SUCCESSFUL_PAYMENT = 'webapp_successful_payment',
  PROMOTED_PRICE = 'promoted_price',
  MAKRO_MEMBER_ID = 'makro_member_id',
  SOURCE = 'source',
  CUSTOMER_ID = 'customer_id',
  NON3P_STORE_CODE = 'non3P_store_code',
  LOCALE = 'locale',
  FE_TYPE = 'fe_type',
  API_VERSION = 'api_version',
  BASKET_SIZE = 'basket_size',
  BASKET_VALUE = 'basket_value',
  APP_VERSION = 'app_version',
  DEVICE_OS = 'device_os',
  MPID = 'mpid'
}

export enum EventMessagesType {
  GO_TO_CHECKOUT = 'go_to_checkout',
  UPDATE_ZIP_AND_SUBDISTRICT = 'update_zip_and_subdistrict',
  DIRECT_CHECKOUT = 'direct_checkout',
  UPDATE_BASKET_CURRENT_STORE_DATA = 'update_basket_current_store_data',
  GO_TO_PDP = 'go_to_pdp',
  GO_TO_BASKET_VOUCHERS = 'go_to_basket_vouchers',
  BASKET_CONTENT_UPDATED = 'basket_content_updated',
  SHOW_BASKET_ERROR = 'show_basket_error',
  SHOW_BASKET_INFO_TOAST = 'show_basket_info_toast',
  SHOW_BASKET_SUCCESS_TOAST = 'show_basket_success_toast',
  DELETE_SELECTED_ITEMS = 'DELETE_SELECTED_ITEMS',
  CLOSE_BASKET_DRAWER = 'CLOSE_BASKET_DRAWER',
  ALCO_TERMS_AND_CONDITIONS_PRESS = 'alco_terms_and_conditions_press',
  GO_TO_HOME_PAGE = 'GO_TO_HOME_PAGE',
  GO_TO_PRO_POINTS_PROMOTIONS_PAGE = 'go_to_pro_points_promotions_page',
  SHOW_PRO_POINTS_ESTIMATIONS_MODAL = 'show_pro_points_estimations_modal',
  SHOW_PRO_POINTS_PROMOTION_DETAILS_MODAL = 'show_pro_points_promotion_details_modal',
  SHOW_PRO_POINTS_PROMOTION_ACTIVATE_NOW_MODAL = 'show_pro_points_promotion_activate_now_modal',
  SHOW_COMPLEX_PROMOTION_DETAILS_MODAL = 'show_complex_promotion_details_modal',
  SHOW_HOW_TO_GET_ECODE_MODAL = 'show_how_to_get_ecode_modal',
  SHOW_ECODE_ACTIVATION_SUCCESS_TOAST = 'show_ecode_activation_success_toast',
  UPDATE_BASKET_PROMOTIONS = 'update_basket_promotions',
  GO_TO_PLP = 'go_to_plp',
  SET_ID_TOKEN = 'set_id_token',
  GET_ID_TOKEN = 'get_id_token',
  RECOMMENDATION_GO_TO_PDP = 'recommendation_go_to_pdp',
  CAROUSEL_SCROLL_EVENT = 'carouselScrollEvent',
  CAROUSEL_IMPRESSION = 'carouselImpression',
  REMOVE_BASKET_PRODUCT = 'remove_product_from_basket',
  BASKET_REFRESHED = 'basket_refreshed',
  ADD_BASKET_ITEM_TO_LOCAL_STORAGE = 'add_basket_item_to_local_storage',
  UPDATE_META_DATA_ZIP_AND_SUBDISTRICT = 'update_meta_data_zip_and_subdistrict',
  SET_CAROUSEL_MAX_ITEMS_COUNT = 'set_carousel_max_items_count',
  OMNI_CHANNELS_VOUCHER_REDEMPTION_ENABLED = 'omni_channels_voucher_redemption_enabled',
  BASKET_ITEMS = 'BASKET_ITEMS',
  SELECT_BASKET_ITEM = 'SELECT_BASKET_ITEM',
  REFRESH_BASKET = 'REFRESH_BASKET',
  TRIGGER_BASKET_ANALYTICS = 'TRIGGER_BASKET_ANALYTICS'
}

export enum CheckoutTypeEnum {
  REGULAR = 'REGULAR',
  DIRECT = 'DIRECT',
  EXPRESS = 'EXPRESS',
  STORE_PICKUP = 'STORE_PICKUP'
}
